import {ProductModel} from "../../models/product";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {ImageUtils} from "../../utils/imageUtils";
import {AddToCartCounter} from "../counters/AddToCartCounter";
import {Image} from "../images/Image";
import {Heading3, Paragraph} from "../text";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  addItemToCart,
  selectIsUpdatingProduct,
  updateItemInCart
} from "../../redux/cart/cartSlice";
import Skeleton from "react-loading-skeleton";
import {fetchProductSucceeded} from "../../redux/product/productSlice";
import {useRouter} from "next/router";
import {useModalRouting} from "../../utils/useModalRouting";
import Link from "next/link";
import {Nutrient} from "../../models/nutrient";
import {selectLoginStatus} from "../../redux/auth/authSlice";

export interface ProductCardViewProps {
  product: ProductModel
  loading: boolean
}

export const ProductCardView: React.FC<ProductCardViewProps> = (props) => {
  const { product } = props

  const router = useRouter()
  const isUpdating = useAppSelector(state => selectIsUpdatingProduct(state, product.id))
  const { createRoute } = useModalRouting();

  const loginStatus = useAppSelector(selectLoginStatus)

  const dispatch = useAppDispatch()
  const price = product.getSelectedSku()?.price
  const currencyCode = product.getSelectedSku()?.currencyCode
  const formattedPrice = CurrencyUtils.stringFromPrice(price, currencyCode)
  const imageUrl = ImageUtils.createUrlFromImage(product.image)
  const nutrients = product.getSelectedSku()?.deliveredMeal?.getNutrients()
  const calories = nutrients?.get('CALORIES')

  return (
    <>
      <div className="relative w-full group" key={product.id}>
        <div className={`inset-y-0 right-0 h-10 lg:h-12 absolute z-10 -mt-3 -mr-3 ${props.loading ? 'hidden' : ''}`}>
          <AddToCartCounter
            isLoading={isUpdating}
            quantity={product.getSelectedSku()?.quantity ?? 0}
            onIncrementClick={() => {
              const item = product.toLineItem()!
              const quantity = item.quantity + 1

              if (loginStatus === 'not_logged_in') {
                router.push(
                  createRoute({ name: 'login', query: { config: 'get_started' }}),
                  undefined,
                  { shallow: true }
                );
              } else if (quantity === 1) {
                dispatch(addItemToCart({ item, product, quantity, storeId: item.storeId! }))
              } else {
                dispatch(updateItemInCart({ item, product, quantity, storeId: item.storeId! }))
              }
            }}
            onDecrementClick={() => {
              const item = product.toLineItem()!
              const quantity = item.quantity - 1

              if (loginStatus === 'not_logged_in') {
                router.push(
                  createRoute({ name: 'login', query: { config: 'get_started' }}),
                  undefined,
                  { shallow: true }
                );
              } else {
                dispatch(updateItemInCart({ item, product, quantity, storeId: item.storeId! }))
              }
            }}
          />
        </div>
        <div className="group">
          <Link
            href={createRoute({ name: 'product_detail', query: { product_id: product?.id } })}
            as={`/product/${product?.id}`}
            shallow={true}
            passHref
          >
            <a onClick={e => {
              e.stopPropagation()
              dispatch(fetchProductSucceeded(product))
            }}>
              { props.loading ? <Skeleton height="0px" className="!aspect-h-1 !aspect-w-1 !rounded-xl" /> : <div className="bg-fg-gray-lightest rounded-lg overflow-hidden">
                {imageUrl ? <Image
                  src={imageUrl}
                  alt={product.name}
                  layout="fill"
                  objectFit="cover"
                  sizeClass="aspect-h-1 aspect-w-1"
                  className="rounded-xl bg-fg-gray-surface"
                /> : <div className="rounded-xl bg-fg-gray-surface aspect-h-1 aspect-w-1" />}
              </div>}
              <Heading3 className="mt-4">{props.loading ? <Skeleton className="!w-12" /> : formattedPrice}</Heading3>
              <Paragraph className="mt-1 text-fg-black">{ props.loading ? <Skeleton className="!w-36" /> : product.name}</Paragraph>
              <Paragraph className={`mt-1 text-fg-gray-dark ${calories ? '' : 'hidden'}`}>{ props.loading ? <Skeleton className="!w-12" /> : `${calories} kcal`}</Paragraph>
            </a>
          </Link>
        </div>
      </div>
    </>
  )
}